import React, { useCallback, useEffect, useState } from 'react';
import { useSensorMaintenanceLayers } from '@/features/sensorMaintenanceLens';
import type { SensorDevice } from '@/features/sensorMaintenanceLens';
import type { ViewState } from './useMapViewState';

interface UseSensorMaintenanceOverlayParams {
	enabled: boolean;
	zoom: number;
	selectedLocation: string;
	setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
	onDeviceHover: (device: SensorDevice | null, x: number, y: number) => void;
	onDeviceClick: () => void;
}

export function useSensorMaintenanceOverlay({
	enabled,
	zoom,
	selectedLocation,
	setViewState,
	onDeviceHover,
	onDeviceClick,
}: UseSensorMaintenanceOverlayParams) {
	const [sensorDevicePopup, setSensorDevicePopup] = useState<SensorDevice | null>(null);

	const { layers: sensorLayers, onMapClick: sensorOnMapClick } = useSensorMaintenanceLayers({
		enabled,
		zoom,
		onDeviceHover,
		onDeviceClick: (device) => {
			onDeviceClick();
			setSensorDevicePopup(device);
		},
		onClusterClick: (clusterLongitude, clusterLatitude, expansionZoom) => {
			setViewState((prev) => ({
				...prev,
				longitude: clusterLongitude,
				latitude: clusterLatitude,
				zoom: Math.min(expansionZoom, 20),
			}));
		},
	});

	useEffect(() => {
		setSensorDevicePopup(null);
	}, [enabled, selectedLocation]);

	const closeSensorDevicePopup = useCallback(() => {
		setSensorDevicePopup(null);
	}, []);

	return { sensorLayers, sensorOnMapClick, sensorDevicePopup, closeSensorDevicePopup };
}
